import { Button, CalendarGrid, RangeCalendar, RangeCalendarCell } from "@beweegbaas/ui";
import { getLocalTimeZone, parseAbsolute } from "@internationalized/date";
import { createFileRoute } from "@tanstack/react-router";
import { useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useDateFormatter } from "#/hooks/use-date-formatter";
import { HistoryBackLink } from "#/components/back-link";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { submitUpdateRapportPeriod } from "#/modules/rapporten/rapporten.functions";
import { RapportInstellingenDialog } from "#/modules/rapporten/components/card.rapport-preferences";
import { RapportenFlowSummary } from "#/modules/rapporten/rapporten-flow-summary";
import { useRapportenFlow } from "#/modules/rapporten/use-rapporten-flow";
import { useAppForm } from "#/modules/util.forms/form";
import type { CalendarDate } from "@internationalized/date";

type SubmitMeta = { submitAction: "update" | "proceed" };
const defaultSubmitMeta: SubmitMeta = { submitAction: "update" };
export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/reports/flow/$id/step/1/",
)({
  component: ReportsStep1Page,
  errorComponent: ReportsStep1Error,
});

function MonthHeader({ date }: { date: CalendarDate }) {
  const formatter = useDateFormatter({ month: "long", year: "numeric" });

  return (
    <div className="text-muted-foreground mt-1 mb-4 text-center text-sm">
      {formatter.format(date.toDate(getLocalTimeZone()))}
    </div>
  );
}

function ReportsStep1Page() {
  const { locale, schoolyear, school, id } = Route.useParams();
  const { rapport } = useRapportenFlow();
  const router = useRouter();
  const submitUpdatePeriod = useServerFn(submitUpdateRapportPeriod);

  const form = useAppForm({
    defaultValues: {
      selectedDates: {
        start: parseAbsolute(rapport?.from ?? "", "UTC"),
        end: parseAbsolute(rapport?.to ?? "", "UTC"),
      },
      name: rapport?.name ?? null,
    },
    onSubmitMeta: defaultSubmitMeta,
    onSubmit: async ({ value, meta }) => {
      try {
        await submitUpdatePeriod({
          data: {
            locale,
            schoolyear,
            school,
            rapportId: id,
            from: value.selectedDates.start.toAbsoluteString(),
            to: value.selectedDates.end.toAbsoluteString(),
            name: value.name ?? "",
            intent: meta.submitAction,
          },
        });
      } catch {
        // toastErrorMiddleware already surfaced the error
        return;
      }
      await router.invalidate();
    },
  });

  return (
    <Page>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Page.Header>
          <HistoryBackLink
            fallback={{
              to: "/$locale/{-$schoolyear}/$school/reports",
              params: { locale, schoolyear, school },
            }}
          />
          <Page.Title>Voor welke periode?</Page.Title>

          <Page.Header.Actions>
            <Page.Header.SecondaryActions>
              <RapportInstellingenDialog />
            </Page.Header.SecondaryActions>
            <Page.Header.MainAction>
              <Button
                className="max-sm:w-full"
                type={"button"}
                size={"xl"}
                onClick={() => {
                  void form.handleSubmit({ submitAction: "proceed" });
                }}
              >
                {m["common.actions.next"]()}
                <IconMaterialSymbolsArrowRightAltRounded />
              </Button>
            </Page.Header.MainAction>
          </Page.Header.Actions>
        </Page.Header>
        <Page.Container>
          <Page.Body>
            <Page.SidebarLayout>
              <div className="grow">
                <form.Field
                  name="selectedDates"
                  children={(field) => (
                    <RangeCalendar
                      aria-label="Trip dates"
                      visibleDuration={{ months: 3 }}
                      firstDayOfWeek="mon"
                      value={field.state.value ?? undefined}
                      onChange={(value) => {
                        if (value?.start && value?.end) {
                          field.handleChange({
                            start: value.start,
                            end: value.end,
                          });
                          void form.handleSubmit({ submitAction: "update" });
                        }
                      }}
                    >
                      {(context) => {
                        const currentMonth = context.state.visibleRange.start;

                        return (
                          <div
                            className="bg-card relative flex flex-col flex-wrap
                                justify-center gap-4 rounded-lg p-4 shadow
                                sm:flex-row"
                          >
                            <Button
                              size="icon"
                              variant="secondary"
                              className="absolute top-3 left-4"
                              onClick={() => {
                                context.state.focusPreviousPage();
                              }}
                            >
                              <IconMaterialSymbolsArrowLeftAltRounded
                                aria-hidden
                                className="size-4"
                              />
                            </Button>

                            <div className="flex flex-col items-center">
                              <MonthHeader date={currentMonth} />
                              <CalendarGrid offset={{ months: 0 }}>
                                {(date) => <RangeCalendarCell date={date} />}
                              </CalendarGrid>
                            </div>
                            <div className="flex flex-col items-center">
                              <MonthHeader date={currentMonth.add({ months: 1 })} />
                              <CalendarGrid offset={{ months: 1 }}>
                                {(date) => <RangeCalendarCell date={date} />}
                              </CalendarGrid>
                            </div>
                            <div className="flex flex-col items-center">
                              <MonthHeader date={currentMonth.add({ months: 2 })} />
                              <CalendarGrid offset={{ months: 2 }}>
                                {(date) => <RangeCalendarCell date={date} />}
                              </CalendarGrid>
                            </div>

                            <Button
                              size="icon"
                              variant="secondary"
                              className="absolute top-3 right-4"
                              onClick={() => context.state.focusNextPage()}
                            >
                              <IconMaterialSymbolsArrowRightAltRounded
                                aria-hidden
                                className="size-4"
                              />
                            </Button>
                          </div>
                        );
                      }}
                    </RangeCalendar>
                  )}
                />
              </div>

              <Page.Sidebar className="max-lg:hidden">
                <RapportenFlowSummary />
              </Page.Sidebar>
            </Page.SidebarLayout>
          </Page.Body>
        </Page.Container>
      </form>
    </Page>
  );
}

function ReportsStep1Error({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}
